import { useMutation } from '@apollo/client';
import type {
  UpdateActionUpdateMutation,
  UpdateActionUpdateMutationVariables,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { UpdateActionUpdateDocument } from '@risksmart-app/web-graphql-client/generated/graphql';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import type { UpdateActionUpdateTRPCInput } from './useUpdateActionUpdateTRPC';
import { useUpdateActionUpdateTRPC } from './useUpdateActionUpdateTRPC';

export const useUpdateActionUpdate = () => {
  const trpcEnabled = useIsFeatureFlagEnabled('trpc');

  // GraphQL mutation (legacy)
  const [updateActionUpdateGraphQL, graphqlState] = useMutation(
    UpdateActionUpdateDocument,
    {
      update: (cache) => {
        evictField(cache, 'action_update');
        evictField(cache, 'action_update_aggregate');
      },
    }
  );

  // tRPC mutation
  const trpcMutation = useUpdateActionUpdateTRPC();

  const updateActionUpdate = async (
    variables: UpdateActionUpdateMutationVariables
  ): Promise<UpdateActionUpdateMutation> => {
    if (trpcEnabled) {
      const trpcInput: UpdateActionUpdateTRPCInput = {
        Id: variables.Id!,
        ParentActionId: variables.ParentActionId!,
        Title: variables.Title!,
        Description: variables.Description!,
        CustomAttributeData: variables.CustomAttributeData ?? null,
        OriginalTimestamp: variables.OriginalTimestamp!,
      };

      return trpcMutation.updateActionUpdate(trpcInput);
    }

    const result = await updateActionUpdateGraphQL({ variables });
    if (!result.data) {
      throw new Error('Failed to update action update');
    }
    if (result.data.update_action_update?.affected_rows !== 1) {
      throw new Error(
        'Records not updated. Record may have been updated by another user'
      );
    }

    return result.data;
  };

  // Return appropriate state based on feature flag
  if (trpcEnabled) {
    return {
      updateActionUpdate,
      loading: trpcMutation.loading,
      error: trpcMutation.error,
    };
  }

  return {
    updateActionUpdate,
    loading: graphqlState.loading,
    error: graphqlState.error,
  };
};
